import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionHistoryApi} from 'api';
import {Accordion, AccordionDetails, AccordionTitle, DataWrapper} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {ConversationSummaryData} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.CONVERSATION_SUMMARY;

type Props = {
    data?: ConversationSummaryData;
    sessionId: string;
    activityId: string;
}

export const ConversationSummaryAccordion: React.FC<Props> = ({data, sessionId, activityId}) => {
    const {t} = useTranslation();

    const {settings} = useContext(UserContext);

    const conversationSummaryEnabled = useMemo(() => !!settings?.featuresAvailability?.conversationSummary, [settings]);

    const {
        send: generateConversationSummary,
        data: conversationSummaryResponse,
        loading,
        reset,
        error
    } = useApiData(sessionHistoryApi.getSessionHistoryConversationSummary);

    const conversationSummary: ConversationSummaryData = useMemo(() =>
        data || conversationSummaryResponse, [data, conversationSummaryResponse]);

    const handleAccordionExpandChange = useCallback((expanded: boolean) => {
        if (conversationSummary) {
            return;
        }

        if (expanded && sessionId && activityId) {
            reset();
            generateConversationSummary(sessionId, activityId);
        }
    }, [conversationSummary, reset, activityId, generateConversationSummary, sessionId]);

    return conversationSummaryEnabled ? (
        <Accordion shadow={false} className='tw-mb-4 tw-mr-4' onChange={handleAccordionExpandChange}>
            <AccordionTitle>{t(T_KEYS.TITLE)}</AccordionTitle>
            <AccordionDetails>
                <DataWrapper isLoading={loading} isError={!!error} errorClassName='tw-text-lg'>
                    {conversationSummary?.summary}
                </DataWrapper>
            </AccordionDetails>
        </Accordion>
    ) : null;
};